import classNames from 'classnames'
import { createContext, useCallback, useContext, useMemo, useState } from 'react'

import {
  desktopBodyScrollCss,
  desktopContentColumnCss,
  desktopFooterHostCss,
  desktopPageHeaderCss,
  mobileDetailCss,
  mobileDetailScrollCss,
  mobileFooterHostCss,
  mobilePageHeaderCss,
  settingsModalDesktopFooterHostHiddenCss,
} from './styles'

const SettingsModalLayoutContext = createContext(null)

const SETTINGS_MODAL_LAYOUT_CLASSES_BY_VARIANT = {
  desktop: {
    column: desktopContentColumnCss,
    scroll: desktopBodyScrollCss,
    footer: desktopFooterHostCss,
    header: desktopPageHeaderCss,
  },
  mobile: {
    column: mobileDetailCss,
    scroll: mobileDetailScrollCss,
    footer: mobileFooterHostCss,
    header: mobilePageHeaderCss,
  },
}

/**
 * Optional layout shell for settings content shown inside SettingsModal.
 * - Desktop: wrapper uses `display: contents` so page header + scroll + footer are grid areas on `desktopShellCss`.
 * - Mobile: when `header` is set, it renders above the scroll region (e.g. memory-route tab strips).
 * - Renders a scrollable main region and a footer host below it (Figma-style fixed footer).
 * - Embed pages keep their <Form> intact and portal Save/Cancel into `footerHost` so submit/reset keep working.
 * Reuse for profile, branding, etc.: read `useSettingsModalLayoutOptional()` and portal form actions when
 * `showFooterHost` is true. For chrome-only differences, use `useIsSettingsModalLayout()`. See `settings_modal.js`.
 */
export const SettingsModalContentColumn = ({
  children,
  header = null,
  showFooterHost = false,
  variant = 'desktop',
  dataTestId,
}) => {
  const [footerHost, setFooterHost] = useState(null)
  const [footerSuppressed, setFooterSuppressed] = useState(false)

  const footerRef = useCallback((node) => {
    setFooterHost(node)
  }, [])

  const effectiveShowFooterHost = showFooterHost && !footerSuppressed

  const value = useMemo(
    () => ({ footerHost, showFooterHost: effectiveShowFooterHost, setFooterSuppressed }),
    [footerHost, effectiveShowFooterHost],
  )

  const {
    column: columnClass,
    scroll: scrollClass,
    footer: footerClass,
    header: headerWrapperClass,
  } = SETTINGS_MODAL_LAYOUT_CLASSES_BY_VARIANT[variant]
  const isDesktop = variant === 'desktop'
  const shouldRenderFooterHost = isDesktop || effectiveShowFooterHost

  return (
    <SettingsModalLayoutContext.Provider value={value}>
      <div className={columnClass} data-testid={dataTestId}>
        {header != null ? (
          <div className={headerWrapperClass} data-testid="settings-modal-page-header">
            {header}
          </div>
        ) : null}
        <div className={scrollClass}>{children}</div>
        {shouldRenderFooterHost ? (
          <div
            ref={footerRef}
            className={classNames(footerClass, {
              [settingsModalDesktopFooterHostHiddenCss]: isDesktop && !effectiveShowFooterHost,
            })}
            data-testid="settings-modal-footer-host"
            aria-hidden={isDesktop && !effectiveShowFooterHost ? true : undefined}
          />
        ) : null}
      </div>
    </SettingsModalLayoutContext.Provider>
  )
}

export const useSettingsModalLayoutOptional = () => useContext(SettingsModalLayoutContext)

/** True when content is under {@link SettingsModalContentColumn} (embedded settings layout). */
export const useIsSettingsModalLayout = () => useContext(SettingsModalLayoutContext) != null
